import { ENV } from '@/lib/config/env';
import { fetchWithAuth } from '@/lib/api/fetch-with-auth';
import { getWorkspaceId } from '@/entities/workspace';
import { i18n } from '@/shared/i18n';
import type { BriefReviewInput } from '../lib/serialize-brief';

interface BriefReviewSuggestion {
  sectionId: string;
  comment: string;
  suggestedContent: string | null;
  /** Exact quote from the section the finding refers to; anchors a text highlight. */
  highlightText: string | null;
  sourceAgentId: string;
}

export interface BriefReviewResult {
  suggestions: BriefReviewSuggestion[];
  contextId: string | null;
}

const BRIEF_REVIEWER_AGENT_ID = 'brief-reviewer-agent';

function buildReasonerHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const workspaceId = getWorkspaceId();
  if (workspaceId) headers['x-selected-workspace-id'] = workspaceId;
  if (ENV.ENABLE_LOCAL_TESTING_AUTHENTICATION && ENV.LOCAL_TOKEN) {
    headers['Authorization'] = ENV.LOCAL_TOKEN;
  }
  return headers;
}

function reasonerUrl(agentId: string): string {
  const baseUrl = ENV.INFERENCE_ENGINE_API_BASE_URL.trim().replace(/\/+$/, '');
  return `${baseUrl}/api/v1/reasoner/${agentId}`;
}

export async function runBriefReview(input: BriefReviewInput): Promise<BriefReviewResult> {
  const response = await fetchWithAuth(reasonerUrl(BRIEF_REVIEWER_AGENT_ID), {
    method: 'POST',
    headers: buildReasonerHeaders(),
    credentials: 'include',
    body: JSON.stringify({ input }),
  });
  if (!response.ok) {
    throw new Error(
      i18n.t('chats:briefAgentReview.briefReviewApi.briefReviewFailed', {
        status: response.status.toString(),
      }),
    );
  }

  const data = (await response.json()) as {
    structuredOutput?: { suggestions?: BriefReviewSuggestion[] };
    context?: { id?: string };
  };
  return {
    suggestions: data.structuredOutput?.suggestions ?? [],
    contextId: data.context?.id ?? null,
  };
}

/**
 * Asks an agent to answer the latest reply in a comment thread. Stateless:
 * the full brief + serialized thread travel in the prompt every time.
 */
export async function runAgentThreadResponse(params: {
  agentId: string;
  briefText: string;
  threadText: string;
  selection?: { selectedText: string; sectionId: string };
}): Promise<{ text: string; contextId: string | null }> {
  const selectionBlock = params.selection
    ? `\n\nThe comment refers to this highlighted excerpt from section ` +
      `${params.selection.sectionId}:\n"${params.selection.selectedText}"`
    : '';
  const prompt =
    `You are replying inside a comment thread on a marketing brief. ` +
    `Answer the latest reply in the thread, in your own voice, concisely.\n\n` +
    `BRIEF:\n${params.briefText}\n\nCOMMENT THREAD:\n${params.threadText}` +
    selectionBlock;

  const response = await fetchWithAuth(reasonerUrl(params.agentId), {
    method: 'POST',
    headers: buildReasonerHeaders(),
    credentials: 'include',
    body: JSON.stringify({ input: { prompt, briefText: params.briefText } }),
  });
  if (!response.ok) {
    throw new Error(
      i18n.t('chats:briefAgentReview.briefReviewApi.agentResponseFailed', {
        status: response.status.toString(),
      }),
    );
  }
  const data = (await response.json()) as { output?: string; context?: { id?: string } };
  return { text: data.output ?? '', contextId: data.context?.id ?? null };
}
